import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/layout/SeoHead';
import { ArrowRight, Camera, Clock, ClipboardCheck, Truck, Handshake, Download } from 'lucide-react';
import QuoteSection from '@/components/sections/QuoteSection';

const BENEFITS = [
  {
    icon: Clock,
    title: '24-48 Hour Turnaround',
    body: 'Closings move fast. So do we. Most properties cleared and ready in two business days.',
  },
  {
    icon: Camera,
    title: 'Photo-Based Quotes',
    body: 'Send pictures, get a number. No site visits required. Move properties through faster.',
  },
  {
    icon: ClipboardCheck,
    title: 'Coordinated Execution',
    body: 'We work directly with your contractors, agents, and closing timelines. One less thing to babysit.',
  },
  {
    icon: Truck,
    title: 'Volume-Friendly Pricing',
    body: 'Recurring partner pricing for investors and agents pushing multiple properties through per quarter.',
  },
];

export default function RealEstatePartners() {
  return (
    <>
      <SeoHead
        title="For Real Estate Investors & Agents | Ohana Property Services"
        description="Fast property cleanouts for real estate investors and agents in Central Indiana. Photo-based quotes, 24-48 hour turnaround, coordinated with your closing timeline."
        canonical="https://ohanapropertyexperts.com/real-estate-partners"
      />

      <section className="relative overflow-hidden bg-ohana-primary text-ohana-bg">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1757348665310-0b68ed0ebc30?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000)',
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-ohana-primary via-ohana-primary/90 to-transparent"
          aria-hidden="true"
        />
        <div className="relative container-ohana pt-24 pb-20 md:pt-32 md:pb-28">
          <p className="overline">For Investors & Agents</p>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            Fast Property Cleanouts for Real Estate Investors and Agents.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-ohana-bg/80 max-w-2xl">
            Photo-based quotes. 24-48 hour turnaround. Reliable execution that moves with your deal flow.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="btn-primary" data-testid="partners-hero-cta">
              Get a Partner Quote
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="section-pad bg-ohana-bg">
        <div className="container-ohana">
          <div className="max-w-3xl">
            <p className="overline">Why Partners Pick Ohana</p>
            <h2 className="h-display text-4xl md:text-5xl mt-3 leading-tight">
              Built for the speed of real estate.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
            {BENEFITS.map((b) => (
              <div key={b.title} className="card-default !p-8" data-testid={`partner-benefit-${b.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-ohana-primary text-ohana-bg">
                  <b.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-serif text-2xl text-ohana-primary leading-snug">{b.title}</h3>
                <p className="mt-3 text-ohana-text-muted leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-ohana-bg" data-testid="landlord-turnover-checklist">
        <div className="container-ohana">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-4">
              <p className="overline">For Landlords &amp; PMs</p>
              <h2 className="h-display text-4xl md:text-5xl mt-3 leading-tight">
                Landlord turnover checklist.
              </h2>
              <p className="mt-5 text-ohana-text-muted text-base md:text-lg leading-relaxed">
                Four phases from move-out to ready-to-rent. Use this as a scoping framework when you call us and we&apos;ll quote against it directly.
              </p>
              <a
                href="/ohana/property-transition-services-guide.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-ohana-primary text-ohana-bg font-bold px-5 py-3 text-sm hover:bg-ohana-primary-dark transition-colors"
                data-testid="landlord-checklist-pdf-cta"
              >
                <Download className="h-4 w-4" />
                Download the full guide (PDF)
              </a>
            </div>

            <div className="lg:col-span-8">
              <div className="rounded-2xl border border-ohana-border bg-white overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-ohana-primary text-ohana-bg">
                    <tr>
                      <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider">Phase</th>
                      <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider">Core tasks</th>
                      <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap">Priority</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ohana-border">
                    {[
                      {
                        phase: 'Preparation',
                        tasks: 'Initial walkthrough, assessment of damages, and item inventory.',
                        priority: 'Critical',
                        tone: 'text-red-700 bg-red-50',
                      },
                      {
                        phase: 'Clearing',
                        tasks: 'Removal of abandoned items, debris, and generic junk.',
                        priority: 'High',
                        tone: 'text-ohana-accent-dark bg-ohana-accent/10',
                      },
                      {
                        phase: 'Deep Clean',
                        tasks: 'Industrial-grade cleaning of floors, walls, and appliances.',
                        priority: 'Standard',
                        tone: 'text-ohana-primary bg-ohana-surface',
                      },
                      {
                        phase: 'Ready',
                        tasks: 'Final inspection and hand-off for new tenant occupancy.',
                        priority: 'Final',
                        tone: 'text-ohana-primary bg-ohana-accent/10',
                      },
                    ].map((row) => (
                      <tr
                        key={row.phase}
                        className="hover:bg-ohana-surface/60 transition-colors"
                        data-testid={`landlord-row-${row.phase.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <td className="px-5 py-4 align-top font-serif text-lg text-ohana-primary whitespace-nowrap">
                          {row.phase}
                        </td>
                        <td className="px-5 py-4 align-top text-ohana-text leading-relaxed">
                          {row.tasks}
                        </td>
                        <td className="px-5 py-4 align-top">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${row.tone}`}
                          >
                            {row.priority}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-ohana-surface" data-testid="partners-operational-support">
        <div className="container-ohana grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-ohana-accent text-white">
              <Handshake className="h-6 w-6" />
            </span>
            <h2 className="mt-5 h-display text-4xl md:text-5xl leading-tight">
              Backed by HOL Creative Solutions.
            </h2>
          </div>
          <div className="lg:col-span-7 text-lg leading-relaxed text-ohana-text space-y-5">
            <p>
              Ohana Property Services operates with operational support from <strong>HOL Creative Solutions</strong>, which means the systems, scheduling, and communication you rely on are built to handle real estate volume.
            </p>
            <p>
              Translation: we answer the phone, we hit our windows, and we don't disappear. Whether you're flipping one property a quarter or running a portfolio, we plug in and execute.
            </p>
          </div>
        </div>
      </section>

      <QuoteSection defaultJobType="Full Property Cleanout" />
    </>
  );
}
